(() => {
  if (customElements.get('personalised-shelf-mock')) return;
  const LIVE_SHELF_TIMEOUT_MS = 30000;
  const FAMILY_PRELOAD_TIMEOUT_MS = 1000;

  class PersonalisedShelfMock extends HTMLElement {
    get minimumProducts() {
      return Math.max(1, Number.parseInt(this.dataset.minimumProducts, 10) || 1);
    }

    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.scheduleCounterUpdate = this.scheduleCounterUpdate.bind(this);
      this.handleCartAdded = this.handleCartAdded.bind(this);
      document.addEventListener('cart:item-added', this.handleCartAdded);
      this.requests = new Set();
      this.familyLoads = new Map();
      this.trackedImpressions = new Set();
      this.desiredFamily = this.dataset.defaultFamily;

      this.addEventListener('click', this.handleClick);
      this.addEventListener('keydown', this.handleKeydown);
      this.bindSliders();
      window.addEventListener('resize', this.scheduleCounterUpdate, { passive: true });
      this.scheduleCounterUpdate();
      this.loadLiveShelf();
    }

    disconnectedCallback() {
      document.removeEventListener('cart:item-added', this.handleCartAdded);
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('keydown', this.handleKeydown);
      this.unbindSliders();
      window.removeEventListener('resize', this.scheduleCounterUpdate);
      this.cancelFamilyPreload?.();
      this.cancelFamilyPreload = null;
      this.requests?.forEach((controller) => controller.abort());
      this.familyLoads?.clear();
      cancelAnimationFrame(this.counterFrame);
      this.initialized = false;
    }

    bindSliders() {
      this.enhanceCoverFallbacks();
      this.unbindSliders();
      this.sliders = [...this.querySelectorAll('.scroll-slider__slider')];
      this.sliders.forEach((slider) => slider.addEventListener('scroll', this.scheduleCounterUpdate, { passive: true }));
    }

    enhanceCoverFallbacks() {
      this.querySelectorAll('[data-shelf-card]').forEach((card) => {
        const link = card.querySelector('.product-item__image-link');
        if (!link || link.dataset.coverFallback) return;
        const showFallback = () => {
          if (link.dataset.coverFallback) return;
          link.dataset.coverFallback = 'true';
          const cover = document.createElement('div');
          cover.className = 'personalised-shelf__cover-fallback';
          const title = document.createElement('strong');
          title.textContent = card.dataset.productTitle;
          const creator = document.createElement('span');
          creator.textContent = card.dataset.productCreator || '';
          cover.append(title, creator);
          link.replaceChildren(cover);
        };
        const image = link.querySelector('img');
        if (!image) showFallback();
        else {
          image.addEventListener('error', showFallback, { once: true });
          if (image.complete && image.currentSrc && !image.naturalWidth) showFallback();
        }
      });
    }

    unbindSliders() {
      this.sliders?.forEach((slider) => slider.removeEventListener('scroll', this.scheduleCounterUpdate));
      this.sliders = [];
    }

    async loadLiveShelf() {
      const endpoint = this.dataset.shelfEndpoint;
      if (!endpoint) return;
      this.setLiveStatus('loading');

      try {
        const response = await this.requestShelf();
        if (!this.isConnected) return;
        if (response.status === 204) {
          this.setLiveStatus('empty');
          this.trackImpression('discover');
          return;
        }
        if (!response.ok) {
          this.setLiveStatus('error');
          return;
        }

        const html = response.html;
        const document = new DOMParser().parseFromString(html, 'text/html');
        const liveContents = document.querySelectorAll('[data-live-shelf-content]');
        const liveContent = liveContents.length === 1 ? liveContents[0] : null;
        const livePanels = liveContent?.querySelectorAll('[data-live-shelf-panel]') || [];
        const livePanel = livePanels.length === 1 ? livePanels[0] : null;
        if (!liveContent || !livePanel || liveContent.querySelector('personalised-shelf-mock')) {
          this.setLiveStatus('invalid');
          console.warn('[Thryft shelf] Shopify returned an invalid recommendation fragment.', {
            contentType: response.headers.get('content-type'),
            liveContentCount: liveContents.length,
            livePanelCount: livePanels.length,
            cardCount: document.querySelectorAll('[data-shelf-card]').length,
            hasNestedShelf: Boolean(liveContent?.querySelector('personalised-shelf-mock')),
            hasUnrenderedLiquid: html.includes('{%') || html.includes('{{'),
          });
          return;
        }
        if (livePanel.querySelectorAll('[data-shelf-card]').length < this.minimumProducts) {
          this.setLiveStatus('empty');
          return;
        }

        const currentContent = this.querySelector('[data-shelf-content]');
        if (!currentContent) {
          this.setLiveStatus('invalid');
          return;
        }
        this.unbindSliders();
        currentContent.replaceWith(liveContent);
        this.dataset.defaultFamily = liveContent.dataset.defaultFamily;
        this.desiredFamily = this.dataset.defaultFamily;
        this.dataset.personalised = 'true';
        this.setLiveStatus('loaded');
        this.setText('.personalised-shelf__preheading', this.dataset.livePreheading);
        this.setText('.personalised-shelf__heading', this.dataset.liveHeading);
        this.setText('.personalised-shelf__subheading', this.dataset.liveSubheading);
        this.bindSliders();
        this.scheduleCounterUpdate();
        this.trackImpression(this.dataset.defaultFamily);
        this.scheduleFamilyPreload();
      } catch (error) {
        // The generic shelf is already visible and remains the safe fallback.
        if (this.isConnected) {
          const status = error.name === 'AbortError' ? 'timeout' : 'error';
          this.setLiveStatus(status);
          console.warn(`[Thryft shelf] Live recommendations ${status}.`, { name: error.name });
        }
      }
    }

    async requestShelf(family = '') {
      const url = new URL(this.dataset.shelfEndpoint, window.location.origin);
      if (url.origin !== window.location.origin) throw new TypeError('Invalid shelf endpoint');
      // `section_id` is reserved by Shopify's Section Rendering API.
      url.searchParams.set('shelf_section_id', this.dataset.sectionId);
      if (family) url.searchParams.set('family', family);
      const controller = new AbortController();
      this.requests.add(controller);
      const timeout = window.setTimeout(() => controller.abort(), LIVE_SHELF_TIMEOUT_MS);
      try {
        const response = await fetch(url, {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
          signal: controller.signal,
        });
        // Keep the timeout and disconnect cancellation active through body reads.
        const html = await response.text();
        return { ok: response.ok, status: response.status, headers: response.headers, html };
      } finally {
        window.clearTimeout(timeout);
        this.requests.delete(controller);
      }
    }

    scheduleFamilyPreload() {
      const placeholder = this.querySelector('[data-shelf-panel-placeholder]');
      if (!placeholder) return;

      const family = placeholder.dataset.family;
      const preload = () => {
        this.cancelFamilyPreload = null;
        if (this.isConnected) this.loadFamily(family);
      };
      if (typeof window.requestIdleCallback === 'function') {
        const handle = window.requestIdleCallback(preload, { timeout: FAMILY_PRELOAD_TIMEOUT_MS });
        this.cancelFamilyPreload = () => window.cancelIdleCallback(handle);
      } else {
        const handle = window.setTimeout(preload, 0);
        this.cancelFamilyPreload = () => window.clearTimeout(handle);
      }
    }

    loadFamily(family) {
      const loadedPanel = this.querySelector(
        `[data-shelf-panel]:not([data-shelf-panel-placeholder])[data-family="${family}"]`
      );
      if (loadedPanel) return Promise.resolve(loadedPanel);

      const pendingLoad = this.familyLoads.get(family);
      if (pendingLoad) return pendingLoad;

      const placeholder = this.querySelector(`[data-shelf-panel-placeholder][data-family="${family}"]`);
      if (!placeholder) return Promise.resolve(null);

      const load = this.fetchFamily(family, placeholder);
      this.familyLoads.set(family, load);
      load.then(() => {
        if (this.familyLoads.get(family) === load) this.familyLoads.delete(family);
      });
      return load;
    }

    async fetchFamily(family, placeholder) {
      this.setPlaceholderState(placeholder, 'loading');
      try {
        const response = await this.requestShelf(family);
        if (!this.isConnected || !placeholder.isConnected) return null;
        if (!response.ok) throw new Error('Shelf request failed');
        const document = new DOMParser().parseFromString(response.html, 'text/html');
        const panels = document.querySelectorAll(`[data-live-shelf-panel][data-family="${family}"]`);
        const panel = panels.length === 1 ? panels[0] : null;
        if (
          !panel
          || document.querySelector('personalised-shelf-mock')
          || panel.querySelectorAll('[data-shelf-card]').length < this.minimumProducts
        ) throw new Error('Invalid shelf panel');
        panel.hidden = this.desiredFamily !== family;
        this.unbindSliders();
        placeholder.replaceWith(panel);
        this.bindSliders();
        return panel;
      } catch {
        if (this.isConnected && placeholder.isConnected) {
          this.setPlaceholderState(placeholder, 'error');
        }
        return null;
      }
    }

    setPlaceholderState(placeholder, state) {
      placeholder.dataset.state = state;
      placeholder.setAttribute('aria-busy', String(state === 'loading'));
      const status = placeholder.querySelector('[data-shelf-placeholder-status]');
      const retry = placeholder.querySelector('[data-shelf-retry]');
      if (status) {
        status.textContent = state === 'error'
          ? 'Recommendations could not load.'
          : 'Loading recommendations…';
      }
      if (retry) retry.hidden = state !== 'error';
      this.scheduleCounterUpdate();
    }

    setLiveStatus(status) {
      this.dataset.liveStatus = status;
    }

    setText(selector, value) {
      const element = this.querySelector(selector);
      if (element && value) element.textContent = value;
    }

    handleClick(event) {
      const retryButton = event.target.closest('[data-shelf-retry]');
      if (retryButton) {
        const placeholder = retryButton.closest('[data-shelf-panel-placeholder]');
        if (placeholder) this.activateFamily(placeholder.dataset.family);
        return;
      }

      const tab = event.target.closest('[data-shelf-tab]');
      if (tab) {
        this.activateFamily(tab.dataset.shelfTab);
        return;
      }

      const moreButton = event.target.closest('[data-shelf-more]');
      if (moreButton) {
        this.toggleMore(moreButton);
        return;
      }

      const lessButton = event.target.closest('[data-shelf-less]');
      if (lessButton) {
        this.dismissCard(lessButton.closest('[data-shelf-card]'));
        return;
      }

      const card = event.target.closest('[data-shelf-card]');
      if (!card || event.target.closest('[data-shelf-more], [data-shelf-less]')) return;
      if (event.target.closest('.product-item__quick-add')) {
        this.pendingCartCard = { card, at: Date.now() };
      } else if (event.target.closest('a[href]')) {
        this.sendFeedback('click', card);
      }
    }

    handleKeydown(event) {
      const activeTab = event.target.closest('[data-shelf-tab]');
      if (!activeTab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

      const tabs = [...this.querySelectorAll('[data-shelf-tab]')];
      const currentIndex = tabs.indexOf(activeTab);
      let nextIndex = currentIndex;

      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = tabs.length - 1;

      event.preventDefault();
      tabs[nextIndex].focus();
      this.activateFamily(tabs[nextIndex].dataset.shelfTab);
    }

    activateFamily(family) {
      this.desiredFamily = family;
      this.querySelectorAll('[data-shelf-tab]').forEach((tab) => {
        const selected = tab.dataset.shelfTab === family;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });

      const placeholder = this.querySelector(`[data-shelf-panel-placeholder][data-family="${family}"]`);
      const visiblePanel = [...this.querySelectorAll('[data-shelf-panel]')]
        .find((panel) => !panel.hidden);
      if (placeholder && visiblePanel && visiblePanel !== placeholder) {
        placeholder.style.minHeight = `${visiblePanel.offsetHeight}px`;
      }
      this.querySelectorAll('[data-shelf-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.family !== family;
      });

      this.scheduleCounterUpdate();
      if (placeholder) {
        this.cancelFamilyPreload?.();
        this.cancelFamilyPreload = null;
        this.loadFamily(family).then((panel) => {
          if (!panel || !this.isConnected) return;
          panel.hidden = this.desiredFamily !== family;
          if (this.desiredFamily === family) {
            this.scheduleCounterUpdate();
            this.trackImpression(family);
          }
        });
        return;
      }
      this.trackImpression(family);
    }

    handleCartAdded(event) {
      const pending = this.pendingCartCard;
      const productId = event.detail?.product?.product_id;
      if (!pending || Date.now() - pending.at > 60000
          || pending.card.dataset.productId !== `gid://shopify/Product/${productId}`) return;
      this.pendingCartCard = null;
      this.sendFeedback('add_to_cart', pending.card);
    }

    toggleMore(button) {
      const card = button.closest('[data-shelf-card]');
      const selected = button.getAttribute('aria-pressed') === 'true';
      const setSelected = (value) => {
        button.setAttribute('aria-pressed', String(value));
        button.classList.toggle('is-selected', value);
      };
      return this.savePreference(card, selected ? 'unmore' : 'more',
        () => setSelected(!selected), () => setSelected(selected),
        selected ? 'updated' : 'moreSaved');
    }

    dismissCard(card) {
      if (!card) return;
      const panel = card.closest('[data-shelf-panel]');
      const replacement = panel?.querySelector('[data-shelf-card][data-shelf-reserve][hidden]');
      return this.savePreference(card, 'less', () => {
        card.hidden = true;
        if (replacement) replacement.hidden = false;
      }, () => {
        card.hidden = false;
        if (replacement) replacement.hidden = true;
      }, 'hidden');
    }

    feedbackStatus(card, key, buttonKey, callback) {
      const status = card.closest('[data-shelf-panel]')?.querySelector('[data-shelf-status]');
      if (!status) return;
      const text = this.dataset[`feedback${key[0].toUpperCase()}${key.slice(1)}`];
      status.replaceChildren(document.createTextNode(text || ''));
      status.hidden = false;
      if (buttonKey) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'personalised-shelf__feedback-action';
        button.textContent = this.dataset[`feedback${buttonKey}`];
        button.addEventListener('click', callback, { once: true });
        status.append(' ', button);
        return button;
      }
    }

    async savePreference(card, action, apply, revert, successKey, eventId = globalThis.crypto?.randomUUID?.()) {
      if (!card || card.dataset.saving === 'true') return;
      card.dataset.saving = 'true';
      const hadFocus = card.contains(document.activeElement);
      apply();
      this.feedbackStatus(card, 'saving');
      this.scheduleCounterUpdate();
      const saved = await this.sendFeedback(action, card, card.dataset.family, eventId);
      card.dataset.saving = 'false';
      if (!this.isConnected) return;
      let control;
      if (saved) {
        const reverse = action.startsWith('un') ? action.slice(2) : `un${action}`;
        control = this.feedbackStatus(card, successKey, 'Undo', () =>
          this.savePreference(card, reverse, revert, apply, 'updated'));
      } else {
        revert();
        control = this.feedbackStatus(card, 'saveError', 'Retry', () =>
          this.savePreference(card, action, apply, revert, successKey, eventId));
      }
      this.scheduleCounterUpdate();
      if (hadFocus && (card.hidden || !saved)) control?.focus();
    }

    trackImpression(family) {
      if (!family || this.trackedImpressions.has(family)) return;
      this.trackedImpressions.add(family);
      const card = this.querySelector(`[data-shelf-panel][data-family="${family}"] [data-shelf-card]`);
      this.sendFeedback('impression', card, family);
    }

    async sendFeedback(action, card, family = card?.dataset.family, eventId = globalThis.crypto?.randomUUID?.()) {
      const endpoint = this.dataset.feedbackEndpoint;
      if (!endpoint || !family || !eventId) return false;
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 8000);
      try {
        const url = new URL(endpoint, window.location.origin);
        if (url.origin !== window.location.origin) return false;
        const response = await fetch(url, {
          method: 'POST', credentials: 'same-origin', keepalive: true,
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
          body: JSON.stringify({ action, eventId, family, productId: card?.dataset.productId || '' }),
        });
        return response.status === 202;
      } catch {
        return false;
      } finally {
        window.clearTimeout(timeout);
      }
    }

    scheduleCounterUpdate() {
      cancelAnimationFrame(this.counterFrame);
      this.counterFrame = requestAnimationFrame(() => this.updateCounter());
    }

    updateCounter() {
      const panel = [...this.querySelectorAll('[data-shelf-panel]')].find((candidate) => !candidate.hidden);
      const counter = this.querySelector('[data-shelf-counter]');
      if (counter && panel?.matches('[data-shelf-panel-placeholder]')) {
        counter.textContent = panel.dataset.state === 'error' ? 'Unavailable' : 'Loading…';
        return;
      }
      const slider = panel?.querySelector('.scroll-slider__slider');
      const cards = panel ? [...panel.querySelectorAll('[data-shelf-card]')].filter((card) => !card.hidden) : [];
      if (!counter || !slider || !cards.length) return;

      const cardWidth = cards[0].getBoundingClientRect().width;
      const gap = parseFloat(getComputedStyle(slider).columnGap) || 0;
      const step = cardWidth + gap;
      const visibleCount = Math.max(1, Math.round((slider.clientWidth + gap) / step));
      const start = Math.min(cards.length - 1, Math.max(0, Math.round(Math.abs(slider.scrollLeft) / step)));
      counter.textContent = `${start + 1}–${Math.min(start + visibleCount, cards.length)} of ${cards.length}`;
    }
  }

  customElements.define('personalised-shelf-mock', PersonalisedShelfMock);
})();
